import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useExpenseCategories,
  useExpenseSubcategories,
} from "@/hooks/data/useEntityCategories";
import { useExpenseTags } from "@/hooks/useExpenseTags";
import { useBulkUpdateExpenses } from "@/hooks/useBulkUpdateExpenses";
import { EXPENSE_STATUS } from "@/constants/expenseStatus";
import { useState, useEffect } from "react";

interface BulkEditExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedExpenseIds: string[];
}

const NO_CHANGE = "__NO_CHANGE__";

const BulkEditExpenseModal = ({
  isOpen,
  onClose,
  selectedExpenseIds,
}: BulkEditExpenseModalProps) => {
  const { data: categories = [] } = useExpenseCategories();
  const { data: tags = [] } = useExpenseTags();
  const bulkUpdateMutation = useBulkUpdateExpenses();

  const [statusId, setStatusId] = useState<string>(NO_CHANGE);
  const [categoryId, setCategoryId] = useState<string>(NO_CHANGE);
  const [subcategoryId, setSubcategoryId] = useState<string>(NO_CHANGE);
  const [tagId, setTagId] = useState<string>(NO_CHANGE);

  const { data: subcategories = [] } = useExpenseSubcategories(
    categoryId !== NO_CHANGE ? categoryId : undefined
  );

  // Resetear subcategoría cuando cambie la categoría
  useEffect(() => {
    if (categoryId !== NO_CHANGE) {
      setSubcategoryId(NO_CHANGE);
    }
  }, [categoryId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validación: Si se cambió categoría, debe seleccionar subcategoría
    if (categoryId !== NO_CHANGE && subcategoryId === NO_CHANGE) {
      return;
    }

    await bulkUpdateMutation.mutateAsync({
      expenseIds: selectedExpenseIds,
      updates: {
        status: statusId !== NO_CHANGE ? statusId : undefined,
        category_id: categoryId !== NO_CHANGE ? categoryId : undefined,
        subcategory_id: subcategoryId !== NO_CHANGE ? subcategoryId : undefined,
        tag_id: tagId !== NO_CHANGE ? tagId : undefined,
      },
    });

    handleClose();
  };

  const handleClose = () => {
    setStatusId(NO_CHANGE);
    setCategoryId(NO_CHANGE);
    setSubcategoryId(NO_CHANGE);
    setTagId(NO_CHANGE);
    onClose();
  };

  // Verificar si hay al menos un cambio válido
  const hasChanges = statusId !== NO_CHANGE || categoryId !== NO_CHANGE || subcategoryId !== NO_CHANGE || tagId !== NO_CHANGE;

  // Validar que si cambió categoría, debe tener subcategoría
  const isValid = categoryId === NO_CHANGE || subcategoryId !== NO_CHANGE;

  const canSubmit = hasChanges && isValid && !bulkUpdateMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-4">
        <DialogHeader>
          <DialogTitle>
            Editar {selectedExpenseIds.length} gasto(s) seleccionado(s)
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Estatus */}
          <div>
            <Label htmlFor="bulk-status">Estatus</Label>
            <Select value={statusId} onValueChange={setStatusId}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHANGE}>
                  <span className="text-muted-foreground italic">Sin cambios</span>
                </SelectItem>
                {EXPENSE_STATUS.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Categoría */}
          <div>
            <Label htmlFor="bulk-category">Categoría</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHANGE}>
                  <span className="text-muted-foreground italic">Sin cambios</span>
                </SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Subcategoría */}
          <div>
            <Label htmlFor="bulk-subcategory">Subcategoría</Label>
            <Select
              value={subcategoryId}
              onValueChange={setSubcategoryId}
              disabled={categoryId === NO_CHANGE}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHANGE}>
                  <span className="text-muted-foreground italic">Sin cambios</span>
                </SelectItem>
                {subcategories.map((sub) => (
                  <SelectItem key={sub.id} value={sub.id}>
                    {sub.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {categoryId !== NO_CHANGE && subcategoryId === NO_CHANGE && (
              <p className="text-xs text-destructive mt-1">
                Debes seleccionar una subcategoría
              </p>
            )}
          </div>

          {/* Etiqueta */}
          <div>
            <Label htmlFor="bulk-tag">Etiqueta</Label>
            <Select value={tagId} onValueChange={setTagId}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHANGE}>
                  <span className="text-muted-foreground italic">Sin cambios</span>
                </SelectItem>
                {tags.map((tag) => (
                  <SelectItem key={tag.id} value={tag.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="pt-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={bulkUpdateMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
            >
              {bulkUpdateMutation.isPending ? "Guardando..." : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BulkEditExpenseModal;
